import React, { createContext, useContext, useState, useRef, useCallback } from 'react'

import AwsS3 from '@uppy/aws-s3'
import Uppy from '@uppy/core'
import config from '../config'
import { createTask } from 'api/repository'
import { useNotification } from 'hooks/useNotification'

const UppyContext = createContext()

// eslint-disable-next-line react/prop-types
export const UppyProvider = ({ children }) => {
  const [uploadBatches, setUploadBatches] = useState([])
  const uploadBatchesRef = useRef([])
  uploadBatchesRef.current = uploadBatches
  const notify = useNotification()

  const removeBatch = useCallback((batchId) => {
    setUploadBatches((prev) => prev.filter((b) => b.id !== batchId))
  }, [])

  const startUpload = useCallback(
    ({ repositoryId, files }) => {
      if (!files || files.length === 0) return null

      const batchId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 11)
      const firstFileName = files[0]?.name || 'Dataset'
      const batchName =
        files.length > 1 ? `${firstFileName} (+${files.length - 1} files)` : firstFileName

      const uppyInstance = new Uppy({
        id: `uppy-${batchId}`,
        autoProceed: false
      }).use(AwsS3, {
        getChunkSize: () => 8 * 1024 * 1024,
        shouldUseMultipart: true,
        endpoint: config.companionUrl
      })

      const newBatch = {
        id: batchId,
        name: batchName,
        repositoryId,
        totalFiles: files.length,
        progress: 0,
        status: 'uploading',
        uppy: uppyInstance
      }

      setUploadBatches((prev) => [...prev, newBatch])

      // Add files to this isolated uppy instance
      files.forEach((fileObj) => {
        try {
          uppyInstance.addFile({
            id: `${batchId}_${fileObj.id || Math.random().toString(36).substring(2, 11)}`,
            name: fileObj.file.name,
            type: fileObj.file.type,
            data: fileObj.file,
            meta: {
              relativePath: fileObj.file.webkitRelativePath || fileObj.path || null,
              option: 'magic-upload',
              repositoryId,
              folderId: null,
              acquiredAt: fileObj.acquiredAt || null,
              module: fileObj.module || null
            }
          })
        } catch (err) {
          console.error('Failed to add file to uppy:', err)
        }
      })

      uppyInstance.on('progress', (progress) => {
        setUploadBatches((prev) =>
          prev.map((b) => (b.id === batchId ? { ...b, progress: Math.round(progress) } : b))
        )
      })

      uppyInstance.on('complete', async (result) => {
        const successfulFiles = result?.successful || []
        const failedFiles = result?.failed || []

        if (successfulFiles.length > 0) {
          const taskFiles = successfulFiles.map((file) => ({
            name: file.name,
            key: file.s3Multipart?.key || file.response?.body?.key,
            meta: file.meta,
            size: file.size
          }))

          try {
            await createTask(repositoryId, {
              name: `Task ${new Date().toISOString()}`,
              type: 'UploadExecutor',
              job_parameters: {
                files: taskFiles,
                folder_id: null
              }
            })
          } catch (err) {
            console.error('Failed to create upload task:', err)
            notify({
              type: 'error',
              message: `Failed to create task for ${batchName}: ${err.message || err}`
            })
          }
        }

        if (failedFiles.length > 0) {
          const names = failedFiles.map((f) => f.name).join(', ')
          notify({
            type: 'error',
            message:
              successfulFiles.length > 0
                ? `Some files failed to upload in ${batchName}: ${names}`
                : `Upload failed for ${batchName}: ${names}`
          })
        } else {
          notify({
            type: 'success',
            message: `Upload completed for ${batchName}, please check processing in Task interface`
          })
        }

        // Giữ batch trong panel (Done / Failed) tới khi người dùng đóng panel
        const status = failedFiles.length > 0 ? 'failed' : 'completed'
        setUploadBatches((prev) => prev.map((b) => (b.id === batchId ? { ...b, progress: 100, status } : b)))

        try {
          uppyInstance.close()
        } catch (e) {
          console.error('Error closing uppy instance:', e)
        }
      })

      uppyInstance.on('error', (err) => {
        console.error('Uppy upload error:', err)
        notify({
          type: 'error',
          message: `Upload error for ${batchName}: ${err.message || err}`
        })
        try {
          uppyInstance.close()
        } catch (e) {
          console.error('Error closing uppy instance:', e)
        }
        setUploadBatches((prev) => prev.map((b) => (b.id === batchId ? { ...b, status: 'failed' } : b)))
      })

      uppyInstance.upload()
      return batchId
    },
    [notify]
  )

  const cancelUpload = useCallback(
    (batchId) => {
      const batch = uploadBatchesRef.current.find((b) => b.id === batchId)
      if (batch?.uppy) {
        try {
          batch.uppy.cancelAll()
          batch.uppy.close()
        } catch (e) {
          console.error('Error cancelling uppy:', e)
        }
      }
      removeBatch(batchId)
    },
    [removeBatch]
  )

  // Xoá các batch đã xong (completed / failed); batch đang upload vẫn giữ
  const clearFinishedUploads = useCallback(() => {
    setUploadBatches((prev) => prev.filter((b) => b.status === 'uploading'))
  }, [])

  const contextValue = {
    uploadBatches,
    startUpload,
    cancelUpload,
    clearFinishedUploads
  }

  return <UppyContext.Provider value={contextValue}>{children}</UppyContext.Provider>
}

export const useUpload = () => {
  return useContext(UppyContext)
}

export const useUppy = () => {
  return useContext(UppyContext)
}
